/**
 * Incremental Route Geometry Updater
 * 
 * Updates route geometry by recalculating only affected segments when stops are added/removed/reordered.
 * This is much faster than recalculating the entire route.
 */

import type { FixedRoute, FixedRouteStop } from '../../types/fixedRoutes';
import { calculateRouteGeometry as osrmCalculateRouteGeometry } from '../../services/routing/osrmClient';
import { calculateRouteGeometry as hereCalculateRouteGeometry } from '../../services/routing/hereClient';
import { computeRouteGeometryHash } from './routeToLineConverter';
import { getDistanceMeters } from '../../components/map/utils/distance';

/**
 * Identify which segments are affected when route geometry is manually edited
 * Since geometry coordinates don't have explicit segment boundaries, we use a conservative approach:
 * - Compare old and new geometry to find edited vertices
 * - Mark segments that likely contain edited vertices as affected
 * - For circulator routes, also check if return segment is affected
 * 
 * @param route Route with updated geometry
 * @param oldGeometry Previous geometry coordinates (before edit)
 * @param newGeometry New geometry coordinates (after edit)
 * @param stops All available stops
 * @returns Array of segment indices that need recalculation
 */
export function identifyAffectedSegments(
  route: FixedRoute,
  oldGeometry: [number, number][],
  newGeometry: [number, number][],
  stops: FixedRouteStop[]
): number[] {
  if (!route.stops || route.stops.length < 2) {
    return [];
  }

  const totalSegments = route.stops.length - 1;
  
  // If geometry lengths differ significantly, mark all segments as affected
  const lengthDiff = Math.abs(newGeometry.length - oldGeometry.length);
  if (lengthDiff > totalSegments * 2) {
    // Significant change - mark all segments
    return Array.from({ length: totalSegments }, (_, i) => i);
  }

  // Find vertices that changed significantly (moved more than 10 meters)
  const EDITED_THRESHOLD_METERS = 10;
  const affectedVertexIndices = new Set<number>();
  
  const minLength = Math.min(oldGeometry.length, newGeometry.length);
  for (let i = 0; i < minLength; i++) {
    const oldCoord = oldGeometry[i];
    const newCoord = newGeometry[i];
    
    if (!oldCoord || !newCoord) continue;
    
    const distance = getDistanceMeters(oldCoord, newCoord);
    if (distance > EDITED_THRESHOLD_METERS) {
      affectedVertexIndices.add(i);
    }
  }

  // If any vertices were added/removed, mark all segments
  if (newGeometry.length !== oldGeometry.length) {
    return Array.from({ length: totalSegments }, (_, i) => i);
  }

  // If no vertices were significantly edited, return empty array
  if (affectedVertexIndices.size === 0) {
    return [];
  }

  // Map edited vertices to segments
  // Since we don't have explicit segment boundaries, we use a heuristic:
  // Distribute vertices evenly across segments, or mark all segments if many vertices are edited
  const editedRatio = affectedVertexIndices.size / newGeometry.length;
  
  if (editedRatio > 0.3) {
    // More than 30% of vertices edited - mark all segments
    return Array.from({ length: totalSegments }, (_, i) => i);
  }

  // For circulator routes, check if return segment is affected
  if (route.isCirculator) {
    const returnSegmentStart = Math.floor(newGeometry.length * 0.8);
    const returnSegmentAffected = Array.from(affectedVertexIndices).some(
      idx => idx >= returnSegmentStart
    );
    
    if (returnSegmentAffected) {
      // Return segment is affected - mark last segment
      return [totalSegments - 1];
    }
  }

  // Conservative approach: if any vertices are edited, mark all segments
  // This ensures correctness at the cost of some extra API calls
  // TODO: Optimize by better mapping vertices to segments
  return Array.from({ length: totalSegments }, (_, i) => i);
}

/**
 * Calculate geometry for specific segments only
 * @param routeStops All stops in route order
 * @param segmentIndices Indices of segments to calculate (0 = segment from stop 0 to stop 1)
 * @returns Map of segment index to its geometry coordinates
 */
async function calculateSpecificSegments(
  routeStops: FixedRouteStop[],
  segmentIndices: number[],
  routingProfile: string = 'driving'
): Promise<Map<number, [number, number][]>> {
  const results = new Map<number, [number, number][]>();
  
  // Sort indices to process in order
  const sortedIndices = [...segmentIndices].sort((a, b) => a - b);
  
  for (let i = 0; i < sortedIndices.length; i++) {
    const segmentIndex = sortedIndices[i];
    
    if (segmentIndex < 0 || segmentIndex >= routeStops.length - 1) {
      continue; // Invalid segment index
    }
    
    const fromStop = routeStops[segmentIndex];
    const toStop = routeStops[segmentIndex + 1];
    
    try {
      const segmentGeometry = await osrmCalculateRouteGeometry(fromStop, toStop, routingProfile);
      if (segmentGeometry && segmentGeometry.length > 0) {
        results.set(segmentIndex, segmentGeometry);
      }
      
      // Rate limiting between segments (except after the last one)
      if (i < sortedIndices.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error(`Failed to recalculate segment ${segmentIndex}:`, error);
      throw error; // Fail fast if any segment fails
    }
  }
  
  return results;
}

/**
 * Calculate geometry for specific segments using HERE (for bus routes)
 */
async function calculateSpecificSegmentsHERE(
  routeStops: FixedRouteStop[],
  segmentIndices: number[]
): Promise<Map<number, [number, number][]>> {
  const results = new Map<number, [number, number][]>();
  
  // Sort indices to process in order
  const sortedIndices = [...segmentIndices].sort((a, b) => a - b);
  
  for (const segmentIdx of sortedIndices) {
    if (segmentIdx < 0 || segmentIdx >= routeStops.length - 1) {
      console.warn(`Invalid segment index: ${segmentIdx}`);
      continue;
    }
    
    const fromStop = routeStops[segmentIdx];
    const toStop = routeStops[segmentIdx + 1];
    
    try {
      const segmentGeometry = await hereCalculateRouteGeometry(fromStop, toStop, true);
      if (segmentGeometry && segmentGeometry.length > 0) {
        results.set(segmentIdx, segmentGeometry);
      }
      
      // Rate limiting between segments (except after the last one)
      if (segmentIdx < sortedIndices[sortedIndices.length - 1]) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error(`Failed to recalculate segment ${segmentIdx}:`, error);
      throw error; // Fail fast if any segment fails
    }
  }
  
  return results;
}

/**
 * Update route geometry by recalculating only affected segments
 * @param route The route with existing geometry
 * @param stops All available stops
 * @param affectedSegmentIndices Indices of segments that need recalculation
 * @returns Updated route with new geometry, or null if update fails
 */
export async function updateRouteGeometryIncremental(
  route: FixedRoute,
  stops: FixedRouteStop[],
  affectedSegmentIndices: number[]
): Promise<FixedRoute | null> {
  // Get route stops in order
  const routeStops = route.stops
    .map((stopConfig) => stops.find((s) => s.id === stopConfig.stopId))
    .filter((stop): stop is FixedRouteStop => stop !== undefined && stop.coordinates !== undefined);

  if (routeStops.length < 2) {
    return null;
  }

  if (!route.geometry?.coordinates || route.geometry.coordinates.length === 0) {
    // No existing geometry - caller should do full recalculation
    return null;
  }

  const totalStopSegments = routeStops.length - 1;
  const existingSegmentGeometries = route.geometry.segmentGeometries;
  if (!existingSegmentGeometries || existingSegmentGeometries.length === 0) {
    // Missing per-segment cache (legacy geometry). Let caller run one full rebuild.
    return null;
  }

  const normalizedAffectedIndices = new Set(
    affectedSegmentIndices.filter(
      (idx) => Number.isInteger(idx) && idx >= 0 && idx < totalStopSegments
    )
  );

  // Route exactly the legs we need: the affected leg(s) PLUS any unchanged leg whose cached
  // geometry is missing/empty. Previously a partial cache (e.g. an appended last leg with no
  // cache for the new index) bailed the WHOLE update to a full re-route of every leg — which is
  // what made "add one point" re-route the entire route. Self-healing only the missing legs keeps
  // the work proportional to the change (append routes 1 leg, insert routes the 2 split legs).
  const indicesToRoute = new Set<number>(normalizedAffectedIndices);
  for (let idx = 0; idx < totalStopSegments; idx++) {
    const cached = existingSegmentGeometries[idx];
    if (!cached || cached.length === 0) {
      indicesToRoute.add(idx);
    }
  }
  const indicesToRouteArr = Array.from(indicesToRoute);

  // Use HERE for bus routes, OSRM for regular routes. Calculate only the legs identified above.
  let newSegmentGeometries: Map<number, [number, number][]>;
  try {
    if (route.isBusRoute) {
      // Use HERE for bus routes
      newSegmentGeometries = await calculateSpecificSegmentsHERE(routeStops, indicesToRouteArr);
    } else {
      // Use OSRM for regular routes
      newSegmentGeometries = await calculateSpecificSegments(routeStops, indicesToRouteArr, 'driving');
    }
  } catch (error) {
    console.error('Failed to calculate affected segments, falling back to full recalculation');
    return null; // Fall back to full recalculation
  }

  const mergedSegmentGeometries: [number, number][][] = Array.from(
    { length: totalStopSegments },
    (_, idx) => existingSegmentGeometries[idx]
  );

  for (const idx of indicesToRouteArr) {
    const updatedSegment = newSegmentGeometries.get(idx);
    if (!updatedSegment || updatedSegment.length === 0) {
      return null;
    }
    mergedSegmentGeometries[idx] = updatedSegment;
  }

  let returnSegmentGeometry = route.geometry.returnSegmentGeometry;
  if (route.isCirculator && routeStops.length >= 2) {
    const lastStop = routeStops[routeStops.length - 1];
    const firstStop = routeStops[0];

    // Return leg only needs recalculation when first/last anchoring segments changed
    // or when we don't have a cached return leg yet.
    const isReturnSegmentAffected =
      indicesToRoute.has(0) ||
      indicesToRoute.has(routeStops.length - 2) ||
      !returnSegmentGeometry ||
      returnSegmentGeometry.length === 0;

    if (isReturnSegmentAffected) {
      try {
        const returnSegment = route.isBusRoute
          ? await hereCalculateRouteGeometry(lastStop, firstStop, true)
          : await osrmCalculateRouteGeometry(lastStop, firstStop, 'driving');
        if (returnSegment && returnSegment.length > 0) {
          returnSegmentGeometry = returnSegment;
        } else {
          return null;
        }
      } catch (error) {
        console.error('Failed to recalculate return segment:', error);
        return null;
      }
    }
  } else {
    returnSegmentGeometry = undefined;
  }

  // Combine merged segment cache into a single coordinate sequence
  let coordinates: [number, number][] = [];
  const segmentsToMerge = returnSegmentGeometry
    ? [...mergedSegmentGeometries, returnSegmentGeometry]
    : mergedSegmentGeometries;

  for (const segment of segmentsToMerge) {
    if (coordinates.length > 0 && segment.length > 0) {
      const lastPoint = coordinates[coordinates.length - 1];
      const firstPoint = segment[0];
      if (
        Math.abs(lastPoint[0] - firstPoint[0]) < 0.0001 &&
        Math.abs(lastPoint[1] - firstPoint[1]) < 0.0001
      ) {
        coordinates = [...coordinates, ...segment.slice(1)];
      } else {
        coordinates = [...coordinates, ...segment];
      }
    } else {
      coordinates = [...coordinates, ...segment];
    }
  }
  
  // Ensure circulator routes form a closed loop
  if (route.isCirculator && coordinates.length > 0) {
    const firstPoint = coordinates[0];
    const lastPoint = coordinates[coordinates.length - 1];
    if (
      Math.abs(lastPoint[0] - firstPoint[0]) > 0.0001 ||
      Math.abs(lastPoint[1] - firstPoint[1]) > 0.0001
    ) {
      coordinates.push([firstPoint[0], firstPoint[1]]);
    }
  }
  
  // Generate new geometry hash (stop order + waypoints + route flags)
  const currentStopsHash = computeRouteGeometryHash(route, routeStops);
  
  // Update route with new geometry
  return {
    ...route,
    geometry: {
      coordinates,
      calculatedAt: Date.now(),
      stopsHash: currentStopsHash,
      segmentGeometries: mergedSegmentGeometries,
      returnSegmentGeometry,
    },
  };
}

